import {filterBy} from "@progress/kendo-data-query"
import {Button} from "@progress/kendo-react-buttons"
import {DateRangePicker, DateRangePickerChangeEvent, SelectionRange} from "@progress/kendo-react-dateinputs"
import {Field, Form, FormElement} from "@progress/kendo-react-form"
import {Input} from "@progress/kendo-react-inputs"
import {useInternationalization} from "@progress/kendo-react-intl"
import {MDBCol, MDBRow} from "mdbreact"
import {useEffect, useState} from "react"
import {useDispatch} from "react-redux"
import {fetchOverview, Overview, OverviewParams, setOverviewFilters} from "../../containers/MotBooking/bookingSlice"
import {RootState} from "../../rootReducer"
import {useAppSelector} from "../../store"
import DealerDropdown from "../Shared/dealerDropdown"
import OverviewDetails from "./Details"

interface FormattedOverview extends Overview {
    flatOverview: string | null
}

const OverviewFilters = () => {
    const dispatch = useDispatch()
    const intl = useInternationalization()
    const selectedDealer = useAppSelector((state: RootState) => state.application.selectedSingleDealer)
    const showBreakDialog = useAppSelector((state: RootState) => state.containers.motBooking.showBreakDialog)
    const overviewFilters = useAppSelector((state: RootState) => state.containers.motBooking.overviewFilters)
    const overviews = useAppSelector((state: RootState) => state.containers.motBooking.overviews)

    const [dateValidation, setDateRangeValid] = useState(true)
    const [dateRangeValue, setDateRange] = useState<SelectionRange>(overviewFilters.dateRange)
    const [filterParams, setFilterParams] = useState<OverviewParams>({
        dateRange: overviewFilters.dateRange,
        selectedDealer: selectedDealer,
    })
    const [filteredOverviews, setFilteredOverviews] = useState<FormattedOverview[] | null>(null)

    useEffect(() => {
        setFilteredOverviews(
            overviews.map(overview => ({
                ...overview,
                flatOverview: null,
            })),
        )
    }, [overviews])

    const onDateChange = (event: DateRangePickerChangeEvent) => {
        event.value.start === null || setDateRangeValid(event.value.end !== null)
        setDateRange(event.value)

        setFilterParams((prevValue: any) => ({
            ...prevValue,
            dateRange: event.value,
        }))
    }

    const onChangeHandler = (overviewData: Overview[], e: any): void => {
        const formattedCurrency = (amount: number): string => intl.formatNumber(amount, "c").replace(/,/g, "")
        const formattedPerc = (value: number): string => intl.formatNumber(value, "p").replace(/,/g, "")

        const transformedOverview = overviewData.map(overview => ({
            ...overview,
            flatOverview: `
            ${overview.eventDateString}
            ~${overview.eventTimes}
            ~${overview.breakTimes}
            ~${overview.totalDuration}
            ~${overview.booked}
            ~${overview.noShow}
            ~${overview.cancelled}
            ~${overview.bookedDuration}
            ~${overview.capacity}
            ~${formattedCurrency(overview.dvsaFee)}
            ~${formattedCurrency(overview.bookedTestFee)}
            ~${formattedCurrency(overview.profitorLoss ?? 0)}
            ~${formattedPerc(overview.utilisation / 100)}
            ~${formattedPerc(overview.profitorLossPer ? overview.profitorLossPer / 100 : 0)}
            ~${formattedCurrency(overview.bookedLaneFee)}`,
        }))

        setFilteredOverviews(
            filterBy(transformedOverview, {
                logic: "or",
                filters: [
                    {
                        field: "flatOverview",
                        operator: "contains",
                        value: e.value,
                        ignoreCase: true,
                    },
                ],
            }),
        )
    }

    const submitHandler = () => {
        const overviewParams = {
            dateRange: dateRangeValue,
            selectedDealer: selectedDealer,
        }
        setFilterParams(overviewParams)
        dispatch(setOverviewFilters(overviewParams))
        selectedDealer && dispatch(fetchOverview(overviewParams))
    }

    return (
        <Form
            initialValues={filterParams}
            render={() => (
                <>
                    <div className="d-flex justify-content-between">
                        <MDBRow className="align-items-end mb-2">
                            <MDBCol md="5" sm="5">
                                <Field name={"plannerDealerID"} component={DealerDropdown} />
                            </MDBCol>
                            <MDBCol md="5" sm="4">
                                <DateRangePicker
                                    valid={dateValidation}
                                    value={dateRangeValue}
                                    onChange={onDateChange}
                                />
                            </MDBCol>
                            <MDBCol>
                                <div className="k-form-buttons mt-auto">
                                    <Button
                                        type="submit"
                                        fillMode="flat"
                                        themeColor={"primary"}
                                        onClick={submitHandler}
                                    >
                                        Go
                                    </Button>
                                </div>
                            </MDBCol>
                        </MDBRow>
                        <Form
                            render={() => (
                                <FormElement style={{width: 300}}>
                                    <fieldset className="k-form-fieldset me-3">
                                        <Field
                                            name="overViewSearch"
                                            onChange={e => onChangeHandler(overviews, e)}
                                            component={Input}
                                            className={"ps-0"}
                                            label={"Search Overview"}
                                        />
                                    </fieldset>
                                </FormElement>
                            )}
                        />
                    </div>

                    {filteredOverviews != null && selectedDealer && selectedDealer?.dealerId > 0 && (
                        <div style={{minHeight: "calc(100vh - 230px)", overflowX: "auto"}}>
                            <OverviewDetails overviews={filteredOverviews} showBreakDialog={showBreakDialog} />
                        </div>
                    )}
                </>
            )}
        />
    )
}

export default OverviewFilters
